<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import BrowserWhatsappAPI from 'dashboard/api/channel/browserWhatsapp';
import NextButton from 'dashboard/components-next/button/Button.vue';
import SpinnerLoader from 'dashboard/components-next/spinner/Spinner.vue';

const props = defineProps({
  inbox: {
    type: Object,
    required: true,
  },
});

const { t } = useI18n();
const isLoading = ref(false);
const actionInProgress = ref('');
const sessionPayload = ref(null);
let refreshTimer;

const session = computed(() => sessionPayload.value?.session || {});
const sessionRecord = computed(
  () => sessionPayload.value?.session_record || {}
);
const lastError = computed(() => sessionRecord.value?.last_error);
const qrCode = computed(() => sessionPayload.value?.qr_code);
const me = computed(() => session.value?.me || {});
const status = computed(() => session.value?.status || 'UNKNOWN');
const isWorking = computed(() => status.value === 'WORKING');
const provisioningStatus = computed(() => sessionRecord.value?.status);
const providerSessionId = computed(
  () => sessionRecord.value?.provider_session_id
);
const needsProviderSession = computed(() => !providerSessionId.value);
const needsProviderApp = computed(
  () =>
    Boolean(providerSessionId.value) && provisioningStatus.value !== 'created'
);
const isProvisioningPending = computed(() =>
  ['pending', 'processing'].includes(provisioningStatus.value)
);
const isProvisioningFailed = computed(
  () => provisioningStatus.value === 'failed'
);

const qrCodeImage = computed(() => {
  if (!qrCode.value?.data) return '';
  const mimetype = qrCode.value.mimetype || 'image/png';
  return `data:${mimetype};base64,${qrCode.value.data}`;
});

const actions = computed(() => {
  if (needsProviderSession.value) {
    return [
      {
        key: 'create_session',
        label: t('INBOX_MGMT.BROWSER_WHATSAPP.ACTIONS.CREATE_SESSION'),
      },
    ];
  }

  if (needsProviderApp.value) {
    return [
      {
        key: 'create_app',
        label: t('INBOX_MGMT.BROWSER_WHATSAPP.ACTIONS.CREATE_APP'),
      },
    ];
  }

  return [
    { key: 'start', label: t('INBOX_MGMT.BROWSER_WHATSAPP.ACTIONS.START') },
    {
      key: 'restart',
      label: t('INBOX_MGMT.BROWSER_WHATSAPP.ACTIONS.RESTART'),
    },
    { key: 'logout', label: t('INBOX_MGMT.BROWSER_WHATSAPP.ACTIONS.LOGOUT') },
    { key: 'stop', label: t('INBOX_MGMT.BROWSER_WHATSAPP.ACTIONS.STOP') },
  ];
});

const isActionDisabled = action =>
  Boolean(actionInProgress.value) ||
  (['create_session', 'create_app'].includes(action.key) &&
    isProvisioningPending.value);

const fetchSession = async ({ silent = false } = {}) => {
  if (!silent) isLoading.value = true;
  try {
    const response = await BrowserWhatsappAPI.getSession(props.inbox.id);
    sessionPayload.value = response.data;
  } catch (error) {
    useAlert(
      error.message || t('INBOX_MGMT.BROWSER_WHATSAPP.API.ERROR_MESSAGE')
    );
  } finally {
    isLoading.value = false;
  }
};

const performAction = async action => {
  actionInProgress.value = action;
  try {
    await BrowserWhatsappAPI.performAction(props.inbox.id, action);
    useAlert(t('INBOX_MGMT.BROWSER_WHATSAPP.API.ACTION_SUCCESS'));
    await fetchSession({ silent: true });
  } catch (error) {
    useAlert(
      error.message || t('INBOX_MGMT.BROWSER_WHATSAPP.API.ACTION_ERROR')
    );
  } finally {
    actionInProgress.value = '';
  }
};

onMounted(() => {
  fetchSession();
  refreshTimer = window.setInterval(
    () => fetchSession({ silent: true }),
    15000
  );
});

onBeforeUnmount(() => {
  if (refreshTimer) window.clearInterval(refreshTimer);
});
</script>

<template>
  <div class="mx-auto w-full max-w-5xl">
    <div v-if="isLoading" class="flex h-64 w-full items-center justify-center">
      <SpinnerLoader :size="28" class="text-n-blue-9" />
    </div>

    <div
      v-else
      class="grid grid-cols-1 justify-center gap-8 lg:grid-cols-[minmax(0,40rem)_26rem]"
    >
      <section class="rounded-xl border border-n-weak bg-n-solid-1 p-6">
        <div class="mb-6 flex items-start justify-between gap-5">
          <div>
            <h2 class="text-base font-medium text-n-slate-12">
              {{ $t('INBOX_MGMT.BROWSER_WHATSAPP.LOGIN.TITLE') }}
            </h2>
            <p class="mb-0 mt-1 text-sm text-n-slate-11">
              {{ $t('INBOX_MGMT.BROWSER_WHATSAPP.LOGIN.DESCRIPTION') }}
            </p>
          </div>
          <span
            class="rounded-full px-2.5 py-1 text-xs font-medium"
            :class="
              isWorking
                ? 'bg-n-teal-3 text-n-teal-11'
                : 'bg-n-amber-3 text-n-amber-11'
            "
          >
            {{ status }}
          </span>
        </div>

        <div v-if="isWorking" class="grid gap-4 md:grid-cols-2">
          <div class="rounded-lg border border-n-weak p-4">
            <div class="text-xs uppercase text-n-slate-10">
              {{ $t('INBOX_MGMT.BROWSER_WHATSAPP.LOGIN.PUSH_NAME') }}
            </div>
            <div class="mt-1 text-sm font-medium text-n-slate-12">
              {{ me.pushName || '-' }}
            </div>
          </div>
          <div class="rounded-lg border border-n-weak p-4">
            <div class="text-xs uppercase text-n-slate-10">
              {{ $t('INBOX_MGMT.BROWSER_WHATSAPP.LOGIN.WHATSAPP_ID') }}
            </div>
            <div class="mt-1 break-all text-sm font-medium text-n-slate-12">
              {{ me.id || me.jid || '-' }}
            </div>
          </div>
          <div class="rounded-lg border border-n-weak p-4 md:col-span-2">
            <div class="text-xs uppercase text-n-slate-10">
              {{ $t('INBOX_MGMT.BROWSER_WHATSAPP.LOGIN.SESSION_NAME') }}
            </div>
            <div class="mt-1 break-all text-sm font-medium text-n-slate-12">
              {{ sessionRecord.session_name || session.name || '-' }}
            </div>
          </div>
        </div>

        <div
          v-else
          class="flex min-h-80 flex-col items-center justify-center rounded-lg border border-dashed border-n-weak p-6"
        >
          <div
            v-if="isProvisioningPending || isProvisioningFailed"
            class="max-w-md text-center"
          >
            <div class="text-sm font-medium text-n-slate-12">
              {{
                isProvisioningFailed
                  ? $t('INBOX_MGMT.BROWSER_WHATSAPP.LOGIN.PROVISIONING_FAILED')
                  : $t('INBOX_MGMT.BROWSER_WHATSAPP.LOGIN.PROVISIONING_PENDING')
              }}
            </div>
            <p class="mb-0 mt-2 text-sm text-n-slate-11">
              {{
                isProvisioningFailed
                  ? $t(
                      'INBOX_MGMT.BROWSER_WHATSAPP.LOGIN.PROVISIONING_FAILED_HELP'
                    )
                  : $t(
                      'INBOX_MGMT.BROWSER_WHATSAPP.LOGIN.PROVISIONING_PENDING_HELP'
                    )
              }}
            </p>
            <p
              v-if="isProvisioningFailed && lastError"
              class="mb-0 mt-3 break-words rounded-lg bg-n-ruby-2 px-3 py-2 text-sm text-n-ruby-11"
            >
              {{ lastError }}
            </p>
          </div>
          <img
            v-else-if="qrCodeImage"
            :src="qrCodeImage"
            class="size-64 rounded-lg bg-white p-3"
            alt=""
          />
          <div v-else class="text-center text-sm text-n-slate-11">
            {{ $t('INBOX_MGMT.BROWSER_WHATSAPP.LOGIN.QR_UNAVAILABLE') }}
          </div>
          <p
            v-if="!isProvisioningPending && !isProvisioningFailed"
            class="mb-0 mt-4 max-w-md text-center text-sm text-n-slate-11"
          >
            {{ $t('INBOX_MGMT.BROWSER_WHATSAPP.LOGIN.QR_HELP') }}
          </p>
        </div>
      </section>

      <aside class="rounded-xl border border-n-weak bg-n-solid-1 p-6">
        <h2 class="text-base font-medium text-n-slate-12">
          {{ $t('INBOX_MGMT.BROWSER_WHATSAPP.ACTIONS.TITLE') }}
        </h2>
        <div class="mt-6 flex flex-col gap-4">
          <NextButton
            v-for="action in actions"
            :key="action.key"
            :label="action.label"
            :is-loading="actionInProgress === action.key"
            :disabled="isActionDisabled(action)"
            slate
            outline
            type="button"
            @click="performAction(action.key)"
          />
          <NextButton
            :label="$t('INBOX_MGMT.BROWSER_WHATSAPP.ACTIONS.REFRESH')"
            :is-loading="isLoading"
            blue
            type="button"
            @click="fetchSession()"
          />
        </div>
      </aside>
    </div>
  </div>
</template>
